import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import { useApolloClient } from '@apollo/client';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import useApolloClientAndMsalAuth from 'customHooks/useApolloClientAndMsalAuth';
import { useSearchParams } from 'customHooks/useSearchParams';
import {
	AREA_CONVERSION_FACTOR,
	TEMPORAL_PLACES_ENUMS,
	TOTAL_SECONDS_IN_A_DAY,
} from 'utils/constants';
import {
	flatMultiPolygonData,
	getFullDate,
	latLngConversion,
} from 'utils/helpers';

import { trackMachineTelematics } from 'thirdParty/firebaseAnalytics';

import { Layer, PointData } from 'interfaces/maps.interface';

import { redirect } from 'actions/navigation';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import useIsUserOnTab from 'customHooks/useIsUserOnTab';
import {
	AllSegmentTypes,
	PlaceEntry,
	PointTagDataInterface,
	PointsResponseInterface,
	TimelineDataInterface,
} from 'modules/Telematics/types';
import {
	isLocationObjectDifferent,
	processTagsData,
} from 'modules/Telematics/utils';
import { setLoaderOff, setLoaderOn } from 'reducers/global.reducer';
// import { smoothlyPanMapsTo } from 'utils/customPanTo';

import LeftPanel from 'modules/Telematics/components/LeftPanel';
import { ALL_DRY_PATH_TAGS } from 'modules/Telematics/constants';
import { usePrevious } from 'modules/Telematics/usePrevious';
// import AlertModal from 'widgets/AlertModal';
import {
	executeFetchAllMachineIds,
	executeFetchDeviceHealth,
	executeFetchGeometry,
	getEntitiesByTimeRange,
} from './queries';
// import GoogleMapView, { mapOptions } from './components/GoogleMapView';
import GoogleMapView from './components/GoogleMapView';

import TelematicsRightPanel from './components/TelematicsRightPanel';

type TelematicsViewProps = {
	mapLayers: Layer[];
	selectedLayer: Layer | null;
	setZoom: (a: number) => void;
	setSelectedLayer?: (a: Layer) => void;
	zoom: number;
};

export interface DeviceHealthData {
	responseStatus: {
		status: 'SUCCESS' | 'FAILED' | string;
		statusReason: 'NO_REQUEST_STATUS_REASON' | string;
	};
	isMalfunctioned: boolean;
	deviceStatus: {
		uuid: string;
		deviceId: string;
		machineNo: string;
		seqNo: number;
		iotDeviceStatus:
			| 'DEVICE_STATUS_GOOD'
			| 'NO_DEVICE_STATUS'
			| 'DEVICE_STATUS_ACTIVE'
			| 'DEVICE_STATUS_MALFUNCTIONED'
			| 'DEVICE_STATUS_GOOD'
			| 'DEVICE_STATUS_BAD'
			| 'DEVICE_STATUS_VERY_BAD';
		averageFrequency: number;
		averageDistance: number;
		millisSinceLastPing: number;
		timeRange: {
			startTime: { seconds: number };
			endTime: { seconds: number };
		};
	}[];
}

const getMaxEndDate = (d: Date) => {
	const date = new Date(d);
	const res = date.setTime(
		date.getTime() - 2 * TOTAL_SECONDS_IN_A_DAY * 1000,
	);
	const newDate = new Date(res);
	return getFullDate(newDate);
};

const TelematicsView: React.FC<TelematicsViewProps> = (): JSX.Element => {
	const { enqueueSnackbar } = useSnackbar();

	const searchParams = useSearchParams();
	const { client } = useApolloClientAndMsalAuth();
	const apolloClient = useApolloClient(client);
	const dispatch = useAppDispatch();

	const mapsRef: any = useRef(null);
	const googleRef: any = useRef(null);
	const liveMachinesRef: any = useRef({});
	const tilesPlaceIdsRef: any = useRef({});

	const machineId = useAppSelector((s) => s.router.location.query.machineId);
	const currentLocation = useAppSelector((s) => s.router.location.query);
	const prevLocation = usePrevious(
		useAppSelector((s) => s.router.location.query),
	);

	const paramStartDate = useAppSelector(
		(s) => s.router.location.query.startDate,
	);
	const paramEndDate = useAppSelector((s) => s.router.location.query.endDate);
	const rawDataFlag = useAppSelector((s) => s.router.location.query.rawData);

	// Date format for input - YYYY-MM-DD
	const [startDate, setStartDate] = useState(
		paramStartDate || getMaxEndDate(new Date()),
	);
	const [endDate, setEndDate] = useState(
		paramEndDate || getFullDate(new Date()),
	);
	const [dryRunData, setDryRunData] = useState<
		Array<{
			placeId: string | number;
			geometry: { lat: number; lng: number }[];
		}>
	>([]);
	const [polyPathData, setPolyPathData]: Array<any> = useState([]);
	const [machinePointsData, setMachinePointsData] = useState<
		Record<string, PointData>
	>({});
	const [machinePolygonData, setMachinePolygonData] = useState([]);
	const [zoom, setZoom] = useState(17);
	const [machineInput, setMachineInput] = useState(machineId);
	const [selectedPolygon, setSelectedPolygon] = useState('');

	const [deviceHealthData, setDeviceHealthData] =
		useState<DeviceHealthData | null>(null);

	// const [, setIsUserOnTabModalVisible] = useState(false);
	const [isRawDataShown, setIsRawDataShown] = useState(
		rawDataFlag === 'true' || rawDataFlag === 'True',
	);
	const [machineIdList, setMachineIdList] = useState([]);

	const [timelineData, setTimelineData] = useState<TimelineDataInterface[]>(
		[],
	);
	const [selectedDryPath, setSelectedDryPath] = useState(undefined);

	const { isUserOnTab, setIsCheckingForActivity } = useIsUserOnTab(30000);

	const handleLiveDataChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setIsRawDataShown(event.target.checked);
	};

	const getMachineList = async () => {
		dispatch(setLoaderOn());
		try {
			const response = await executeFetchAllMachineIds({
				apolloClient,
			});
			if (
				response?.data?.getAllMachineIds &&
				response?.data?.getAllMachineIds.responseStatus.status ===
					'SUCCESS'
			) {
				const list = response?.data?.getAllMachineIds.machineIds;
				setMachineIdList(list);
			}
		} finally {
			dispatch(setLoaderOff());
		}
	};

	const getDeviceHealthData = async () => {
		dispatch(setLoaderOn());
		try {
			const response = await executeFetchDeviceHealth({
				machineInput: machineId,
				startDate,
				endDate,
				apolloClient,
			});
			if (response?.data?.checkDeviceHealth) {
				const healthData: DeviceHealthData =
					response?.data?.checkDeviceHealth;
				if (healthData.responseStatus.status === 'SUCCESS') {
					setDeviceHealthData(healthData);
				}
			}
		} finally {
			dispatch(setLoaderOff());
		}
	};

	const getPointsData = async ({
		env,
		entityType,
	}: {
		env: any;
		entityType: string;
	}) => {
		dispatch(setLoaderOn());
		try {
			const response = await getEntitiesByTimeRange({
				entityType,
				machineInput: machineId,
				startDate,
				endDate,
				apolloClient,
			});
			if (
				response?.data?.getEntitiesByTimeRange?.responseStatus
					?.status === 'SUCCESS'
			) {
				if (response.data.getEntitiesByTimeRange?.responseMatrix) {
					const responseMatrix: PointsResponseInterface = JSON.parse(
						response.data.getEntitiesByTimeRange.responseMatrix,
					);
					let payload: Record<string, PointData> = {};
					responseMatrix.MachineNumber.forEach((mchn, index) => {
						const key = `${responseMatrix.SequenceNumber[index]}-${env.namespace}-${env.sourceType}-${env.visibleType}`;
						const geometry = {
							lat: responseMatrix.Latitude[index],
							lng: responseMatrix.Longitude[index],
						};
						const tagData: PointTagDataInterface = {
							machineNumber: responseMatrix.MachineNumber[index],
							IOTDeviceStatus:
								responseMatrix.IOTDeviceStatus[index],
							SupplyGood: responseMatrix.SupplyGood[index],
							DeviceId: responseMatrix.DeviceId[index],
							DeviceNetworkStatusList:
								responseMatrix.DeviceNetworkStatusList[index],
							Latitude: responseMatrix.Latitude[index],
							LocationArea: responseMatrix.LocationArea[index],
							GsmSignalStrength:
								responseMatrix.GsmSignalStrength[index],
							Accuracy: responseMatrix.Accuracy[index],
							InternalTemp: responseMatrix.InternalTemp[index],
							LoadedBatteryVoltage:
								responseMatrix.LoadedBatteryVoltage[index],
							SequenceNumber:
								responseMatrix.SequenceNumber[index],
							IgnitionOn: responseMatrix.IgnitionOn[index],
							HeadingDegrees:
								responseMatrix.HeadingDegrees[index],
							SprayerOn: responseMatrix.SprayerOn[index],
							Speed: responseMatrix.Speed[index],
							IgnitionStatusList:
								responseMatrix.IgnitionStatusList[index],
							DeviceTowingStatusList:
								responseMatrix.DeviceTowingStatusList[index],
							BatteryPercentage:
								responseMatrix.BatteryPercentage[index],
							ExtPowerStatusList:
								responseMatrix.ExtPowerStatusList[index],
							InternalBatteryStatusList:
								responseMatrix.InternalBatteryStatusList[index],
							Tehsil: responseMatrix.Tehsil[index],
							ExternalPowerConnected:
								responseMatrix.ExternalPowerConnected[index],
							Longitude: responseMatrix.Longitude[index],
							TimeStamp: responseMatrix.TimeStamp[index],
							SpeedLimitStatusList:
								responseMatrix.SpeedLimitStatusList[index],
							MachineNumber: responseMatrix.MachineNumber[index],
							State: responseMatrix.State[index],
							LogReason: responseMatrix.LogReason[index],
							OdometerInKm: responseMatrix.OdometerInKm[index],
							AccidentStatusList:
								responseMatrix.AccidentStatusList[index],
							EngineOn: responseMatrix.EngineOn[index],
							District: responseMatrix.District[index],
							InternalBatteryVoltage:
								responseMatrix.InternalBatteryVoltage[index],
							ExternalVoltage:
								responseMatrix.ExternalVoltage[index],
							Altitude: responseMatrix.Altitude[index],
						};
						payload[key] = {
							placeId:
								responseMatrix.SequenceNumber[index].toString(),
							selectedLayer: {
								PlaceNamespace: env.namespace,
								PlaceSourceType: env.sourceType,
								PlaceVisibleType: env.visibleType,
							},
							placeGeometryResult: {
								geometry,
								geometryType: 'point',
							},
							tagData,
							placeTags: [],
							referenceAttributes: [],
						};
					});
					setMachinePointsData(payload);
				} else {
					enqueueSnackbar('Machine Data Unavailable', {
						autoHideDuration: 4000,
					});
				}
			}
		} catch (err) {
			enqueueSnackbar('Machine Data Unavailable', {
				autoHideDuration: 4000,
			});
		} finally {
			dispatch(setLoaderOff());
		}
	};

	const [totalBookings, setTotalBookings] = useState(0);
	const getPolygonsGeometry = async (environment: any) => {
		const response: any = await executeFetchGeometry({
			environment,
			machineInput: machineId,
			startDate,
			endDate,
			apolloClient,
		});
		if (response?.data?.GetTemporalPlacesByReferences?.places?.length) {
			const polygons: any = [];
			let numberOfBookings = 0;
			response?.data?.GetTemporalPlacesByReferences?.places.forEach(
				(polygonData: any) => {
					const wkbToGeoJSON: any = latLngConversion(
						polygonData.geometry,
					);
					polygonData.referenceAttributes.forEach((attr: any) => {
						if (attr.referenceType === 'RT_BOOKING') {
							numberOfBookings += 1;
						}
					});
					if (wkbToGeoJSON?.geometry) {
						if (wkbToGeoJSON?.type === 'MultiPolygon') {
							const multiPolygonGeometry =
								wkbToGeoJSON?.geometry?.coordinates;
							const flattedMultiPolygonGeometry: any =
								flatMultiPolygonData(
									polygonData,
									multiPolygonGeometry,
								);
							if (
								multiPolygonGeometry?.length &&
								flattedMultiPolygonGeometry
							) {
								polygons.push({
									placeId: polygonData?.placeId,
									geometry: flattedMultiPolygonGeometry,
									polygonAcre:
										flattedMultiPolygonGeometry?.polygonAcre,
									startTime:
										polygonData?.timeRange?.startTime
											?.seconds,
									endTime:
										polygonData?.timeRange?.endTime
											?.seconds,
									polygonType: wkbToGeoJSON?.type,
									placeTags: polygonData?.tags,
									referenceAttributes:
										polygonData?.referenceAttributes,
								});
							}
						} else {
							const totalAcres = Number(
								googleRef.current.geometry.spherical.computeArea(
									new googleRef.current.Polygon({
										paths: wkbToGeoJSON?.geometry,
									}).getPath(),
								) * AREA_CONVERSION_FACTOR,
							).toFixed(2);
							polygons.push({
								placeId: polygonData?.placeId,
								geometry: wkbToGeoJSON?.geometry,
								polygonAcre: totalAcres,
								startTime:
									polygonData?.timeRange?.startTime?.seconds,
								endTime:
									polygonData?.timeRange?.endTime?.seconds,
								polygonType: wkbToGeoJSON?.type,
								placeTags: polygonData?.tags,
								referenceAttributes:
									polygonData?.referenceAttributes,
							});
						}
					}
				},
			);
			setTotalBookings(numberOfBookings);
			// if (polygons?.length) {
			// 	mapsRef?.current?.setCenter(polygons[0]?.geometry?.[0]);
			// }
			setMachinePolygonData(polygons);
		}
	};
	const getTimelineData = async (environment: any) => {
		dispatch(setLoaderOn());
		try {
			const response = await executeFetchGeometry({
				environment,
				machineInput: machineId,
				startDate,
				endDate,
				apolloClient,
			});
			let placesList =
				response?.data?.GetTemporalPlacesByReferences?.places;
			if (placesList.length) {
				let localDryRunData: Array<{
					placeId: string | number;
					geometry: { lat: number; lng: number }[];
				}> = [];
				let localPolyLineData: Array<any> = [];
				let rows: TimelineDataInterface[] = [];

				placesList.forEach((p: PlaceEntry) => {
					if (p?.geometry?.length >= 12000) {
						// Ignoring wrong geometry
						return;
					}
					const { geometry }: any = latLngConversion(p.geometry);
					let tagData = processTagsData(p.tags);
					let row: TimelineDataInterface = {
						startTime: p.timeRange.startTime.seconds * 1000,
						endTime: p.timeRange.endTime.seconds * 1000,
						metadata: {
							geometry,
							type: tagData[
								ALL_DRY_PATH_TAGS.TagProperty_SEGMENT_TYPE
							] as string,
							placeId: p.placeId,
						},
						tagData,
					};

					if (
						tagData[ALL_DRY_PATH_TAGS.TagProperty_SEGMENT_TYPE] ===
						AllSegmentTypes.POLY_SEGMENT
					) {
						let AREA_IN_ACRES = 0;
						const wkbToGeoJSON: any = latLngConversion(p.geometry);
						if (wkbToGeoJSON?.geometry) {
							if (wkbToGeoJSON?.type === 'MultiPolygon') {
								const multiPolygonGeometry =
									wkbToGeoJSON?.geometry?.coordinates;
								const flattedMultiPolygonGeometry: any =
									flatMultiPolygonData(
										{ placeId: p.placeId },
										multiPolygonGeometry,
									);
								if (
									multiPolygonGeometry?.length &&
									flattedMultiPolygonGeometry
								) {
									AREA_IN_ACRES =
										flattedMultiPolygonGeometry?.polygonAcre;
								}
							} else {
								AREA_IN_ACRES = Number(
									Number(
										googleRef.current.geometry.spherical.computeArea(
											new googleRef.current.Polygon({
												paths: wkbToGeoJSON?.geometry,
											}).getPath(),
										) * AREA_CONVERSION_FACTOR,
									).toFixed(2),
								);
							}
						}
						row.AREA_IN_ACRES = AREA_IN_ACRES;
					}
					rows.push(row);
					switch (
						tagData[ALL_DRY_PATH_TAGS.TagProperty_SEGMENT_TYPE]
					) {
						case 'DRY_RUN_SEGMENT':
							localDryRunData.push({
								geometry,
								placeId: p.placeId,
							});
							break;
						case 'POLY_SEGMENT':
							localPolyLineData.push(geometry);
							break;
						default:
					}
				});
				setTimelineData(rows);
				setDryRunData(localDryRunData);
				setPolyPathData(localPolyLineData);
			}
		} catch (err) {
			//
		} finally {
			dispatch(setLoaderOff());
		}
	};

	const fetchMachineData = () => {
		trackMachineTelematics(machineId, startDate, endDate);
		getPolygonsGeometry(TEMPORAL_PLACES_ENUMS.POLYGONS);
		getPointsData({
			env: isRawDataShown
				? TEMPORAL_PLACES_ENUMS.POINTS
				: TEMPORAL_PLACES_ENUMS.PRUNED_POINTS,
			entityType: isRawDataShown
				? TEMPORAL_PLACES_ENUMS.POINTS.visibleType
				: TEMPORAL_PLACES_ENUMS.PRUNED_POINTS.visibleType,
		});
		getTimelineData(TEMPORAL_PLACES_ENUMS.DRY_RUN);
		getDeviceHealthData();
	};

	const updateURLParams = () => {
		searchParams.set('startDate', startDate);
		searchParams.set('endDate', endDate);
		searchParams.set('machineId', machineInput || '');
		searchParams.set(
			'rawData',
			String(isRawDataShown).valueOf() || 'false',
		);
		const newPath = `telematics?${searchParams.toString()}`;
		dispatch(redirect(newPath));
	};

	const handleZoomChange = useCallback(() => {
		if (mapsRef.current === null) return;
		const currentZoom = mapsRef?.current?.getZoom();

		setZoom(currentZoom);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateGoogleRef = useCallback(
		(googleInstance: typeof google.maps) => {
			googleRef.current = googleInstance;
		},
		[],
	);

	const applyFilters = () => {
		updateURLParams();
	};

	const onClickTimeSegment = (segmentData: any) => {
		const geometry = segmentData?.geometry;
		if (segmentData.type === AllSegmentTypes.DRY_RUN_SEGMENT) {
			setSelectedDryPath(segmentData?.placeId);
			mapsRef?.current?.setCenter(geometry[0]);
			// smoothlyPanMapsTo({
			// 	mapInstance: mapsRef.current,
			// 	mapOptions,
			// 	lat: geometry[0].lat,
			// 	lng: geometry[0].lng,
			// 	zoom,
			// });
		} else {
			setSelectedPolygon(segmentData?.placeId);
			if (Array.isArray(geometry) && geometry?.length) {
				mapsRef?.current?.setCenter(geometry[0]);
				// for Polygon
				// smoothlyPanMapsTo({
				// 	mapInstance: mapsRef.current,
				// 	mapOptions,
				// 	lat: geometry[0].lat,
				// 	lng: geometry[0].lng,
				// 	zoom,
				// });
			} else {
				// for MultiPolygon
				if (geometry[segmentData?.placeId]) {
					mapsRef?.current?.setCenter(
						geometry[segmentData?.placeId]?.[0]?.[0],
					);
					// smoothlyPanMapsTo({
					// 	mapInstance: mapsRef.current,
					// 	mapOptions,
					// 	lat: geometry[segmentData?.placeId]?.[0]?.[0].lat,
					// 	lng: geometry[segmentData?.placeId]?.[0]?.[0].lng,
					// 	zoom,
					// });
				}
			}
		}
	};
	const resetData = () => {
		setDeviceHealthData(null);
		setDryRunData([]);
		setPolyPathData([]);
		setMachinePointsData({});
		setMachinePolygonData([]);
		setTotalBookings(0);
		setTimelineData([]);
	};
	useEffect(() => {
		getMachineList();
	}, []);

	useEffect(() => {
		if (prevLocation && currentLocation) {
			const isDifferent = isLocationObjectDifferent(
				prevLocation,
				currentLocation,
			);
			if (isDifferent) {
				resetData();
				fetchMachineData();
				if (machineInput !== currentLocation.machineId)
					setMachineInput(currentLocation.machineId);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentLocation]);

	useEffect(() => {
		if (!isUserOnTab) {
			// setIsUserOnTabModalVisible(true);
			// pause checking
			setIsCheckingForActivity(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUserOnTab]);

	return (
		<div style={{ width: '100%', height: '100vh' }}>
			<Grid
				container
				spacing={0}
				sx={{ height: '100%', flexWrap: 'nowrap' }}
			>
				<div style={{ width: '70%', position: 'relative' }}>
					<GoogleMapView
						handleZoomChange={handleZoomChange}
						isUserOnTab={isUserOnTab}
						liveMachinesRef={liveMachinesRef}
						mapsRef={mapsRef}
						machinePointsData={machinePointsData}
						machinePolygonData={machinePolygonData}
						onMapLoadComplete={fetchMachineData}
						selectedPolygon={selectedPolygon}
						tilesPlaceIdsRef={tilesPlaceIdsRef}
						updateGoogleRef={updateGoogleRef}
						zoom={zoom || 17}
						dryRunData={dryRunData}
						polyPathData={polyPathData}
						selectedDryPath={selectedDryPath}
					/>
				</div>
				<div
					style={{
						width: '30%',
						overflowY: 'scroll',
						padding: '20px',
					}}
				>
					<Box
						sx={{
							marginTop: '10px',
						}}
					>
						<TelematicsRightPanel
							applyFilters={applyFilters}
							startDate={startDate}
							endDate={endDate}
							setStartDateValue={setStartDate}
							setEndDateValue={setEndDate}
							machineInputValue={machineInput}
							onMachineIdChange={(value: any) =>
								setMachineInput(value)
							}
							machineIdList={machineIdList}
							onClickTimeSegment={onClickTimeSegment}
							machinePolygonData={machinePolygonData}
							isRawDataShown={isRawDataShown}
							handleLiveDataChange={handleLiveDataChange}
							deviceHealthData={deviceHealthData}
							timelineData={timelineData}
							totalBookings={totalBookings}
						/>
					</Box>
				</div>
				<div
					style={{ position: 'absolute', top: '10vh', width: 'auto' }}
				>
					<LeftPanel mapsRef={mapsRef} />
				</div>
			</Grid>
			{/* <AlertModal
				heading="Live Feed Paused"
				subHeading="The user was away from the tab. Press OK to continue the live feed."
				buttons={[
					{
						buttonText: 'Ok',
						onClick: () => {
							setIsUserOnTabModalVisible(false);
							// resume checking
							setIsCheckingForActivity(true);
						},
						type: 'contained',
					},
				]}
				open={isUserOnTabModalVisible}
				handleClose={() => {
					setIsUserOnTabModalVisible(false);
					// resume checking
					setIsCheckingForActivity(true);
				}}
			/> */}
		</div>
	);
};

export default TelematicsView;
